import { useEffect, useState, type FormEvent } from "react";
import { AlertTriangle, Download } from "lucide-react";
import {
  Button, Card, EmptyState, ErrorText, FormSection, Input,
  PageHeader, Select, Skeleton, useToast,
} from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch, downloadFile } from "../lib/api";

/** Flat, matching businessSettingsSchema exactly — the PUT body shape. */
interface BusinessSettingsForm {
  legalNameAr: string;
  legalNameEn: string;
  tradeName: string;
  vatNumber: string;
  crNumber: string;
  phone: string;
  email: string;
  website: string;
  buildingNumber: string;
  street: string;
  district: string;
  city: string;
  postalCode: string;
  additionalNumber: string;
  country: string;
  currency: string;
  language: "ar" | "en";
  timezone: string;
}

/** The GET response nests address fields; this flattens it into the form/PUT shape. */
interface BusinessSettingsResponse {
  legalNameAr: string;
  legalNameEn: string;
  tradeName: string | null;
  vatNumber: string | null;
  crNumber: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  address: {
    buildingNumber: string | null;
    street: string | null;
    district: string | null;
    city: string | null;
    postalCode: string | null;
    additionalNumber: string | null;
    country: string;
  };
  currency: string;
  language: string;
  timezone: string;
}

function toForm(r: BusinessSettingsResponse): BusinessSettingsForm {
  return {
    legalNameAr: r.legalNameAr, legalNameEn: r.legalNameEn, tradeName: r.tradeName ?? "",
    vatNumber: r.vatNumber ?? "", crNumber: r.crNumber ?? "", phone: r.phone ?? "",
    email: r.email ?? "", website: r.website ?? "",
    buildingNumber: r.address.buildingNumber ?? "", street: r.address.street ?? "",
    district: r.address.district ?? "", city: r.address.city ?? "",
    postalCode: r.address.postalCode ?? "", additionalNumber: r.address.additionalNumber ?? "",
    country: r.address.country, currency: r.currency,
    language: r.language === "en" ? "en" : "ar", timezone: r.timezone,
  };
}

/**
 * Business identity — the legal facts that end up SNAPSHOTTED onto every
 * invoice issued from this moment forward. Nothing here can reach an invoice
 * that already exists (see docs/INVOICE_RENDERING.md and the invoice
 * snapshot columns) — that immutability is enforced at issuance, not by this
 * screen withholding anything.
 */
export function SettingsPage() {
  const { hasPermission } = useAuth();
  const { language: uiLanguage } = useBranding();
  const { showToast } = useToast();
  const ar = uiLanguage === "ar";

  const [form, setForm] = useState<BusinessSettingsForm | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const canEdit = hasPermission("settings.update");
  const canExport = hasPermission("settings.export");

  const load = async () => {
    setLoadError(null);
    try {
      const r = await apiFetch<BusinessSettingsResponse>("/settings/business");
      setForm(toForm(r));
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : ar ? "تعذّر تحميل الإعدادات." : "Couldn't load settings.");
    }
  };

  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setSaveError(null);
    setSaving(true);
    try {
      await apiFetch("/settings/business", { method: "PUT", body: form });
      showToast("success", ar ? "حُفظت البيانات." : "Saved.");
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : ar ? "تعذّر الحفظ." : "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const set = <K extends keyof BusinessSettingsForm>(key: K, value: BusinessSettingsForm[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  const runExport = async () => {
    setExporting(true);
    try {
      await downloadFile("/settings/export", `laundrypro-export-${new Date().toISOString().slice(0, 10)}.json`);
    } catch (err) {
      showToast("error", err instanceof ApiError ? err.message : ar ? "تعذّر التصدير." : "Couldn't export.");
    } finally {
      setExporting(false);
    }
  };

  if (loadError) {
    return (
      <div className="space-y-4">
        <PageHeader title={ar ? "الإعدادات" : "Settings"} />
        <Card>
          <EmptyState icon={<AlertTriangle className="size-6" />} title={ar ? "تعذّر التحميل" : "Couldn't load"} description={loadError}
            action={<Button size="sm" onClick={load}>{ar ? "إعادة المحاولة" : "Retry"}</Button>} />
        </Card>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-7 w-40" />
        <Card><Skeleton className="h-96 w-full" /></Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader title={ar ? "الإعدادات" : "Settings"} subtitle={ar ? "هوية المنشأة القانونية." : "The business's legal identity."} />

      <form onSubmit={save} className="space-y-4">
        <Card>
          <FormSection legend={ar ? "الاسم" : "Identity"}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input label={ar ? "الاسم القانوني (عربي)" : "Legal name (Arabic)"} value={form.legalNameAr} onChange={(e) => set("legalNameAr", e.target.value)} disabled={!canEdit} required />
              <Input label={ar ? "الاسم القانوني (إنجليزي)" : "Legal name (English)"} value={form.legalNameEn} onChange={(e) => set("legalNameEn", e.target.value)} disabled={!canEdit} />
              <Input label={ar ? "الاسم التجاري" : "Trade name"} value={form.tradeName} onChange={(e) => set("tradeName", e.target.value)} disabled={!canEdit} />
            </div>
          </FormSection>
        </Card>

        <Card>
          <FormSection legend={ar ? "الضريبة والسجل" : "Tax and registration"}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input label={ar ? "الرقم الضريبي" : "VAT number"} value={form.vatNumber} onChange={(e) => set("vatNumber", e.target.value)} disabled={!canEdit}
                helperText={ar ? "١٥ رقمًا." : "15 digits."} />
              <Input label={ar ? "السجل التجاري" : "CR number"} value={form.crNumber} onChange={(e) => set("crNumber", e.target.value)} disabled={!canEdit} />
            </div>
          </FormSection>
        </Card>

        <Card>
          <FormSection legend={ar ? "التواصل" : "Contact"}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input label={ar ? "الجوال" : "Phone"} value={form.phone} onChange={(e) => set("phone", e.target.value)} disabled={!canEdit} />
              <Input label={ar ? "البريد الإلكتروني" : "Email"} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} disabled={!canEdit} />
              <Input label={ar ? "الموقع الإلكتروني" : "Website"} value={form.website} onChange={(e) => set("website", e.target.value)} disabled={!canEdit} />
            </div>
          </FormSection>
        </Card>

        <Card>
          <FormSection legend={ar ? "العنوان الوطني" : "National address"}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input label={ar ? "رقم المبنى" : "Building number"} value={form.buildingNumber} onChange={(e) => set("buildingNumber", e.target.value)} disabled={!canEdit} />
              <Input label={ar ? "الشارع" : "Street"} value={form.street} onChange={(e) => set("street", e.target.value)} disabled={!canEdit} />
              <Input label={ar ? "الحي" : "District"} value={form.district} onChange={(e) => set("district", e.target.value)} disabled={!canEdit} />
              <Input label={ar ? "المدينة" : "City"} value={form.city} onChange={(e) => set("city", e.target.value)} disabled={!canEdit} />
              <Input label={ar ? "الرمز البريدي" : "Postal code"} value={form.postalCode} onChange={(e) => set("postalCode", e.target.value)} disabled={!canEdit} />
              <Input label={ar ? "الرقم الإضافي" : "Additional number"} value={form.additionalNumber} onChange={(e) => set("additionalNumber", e.target.value)} disabled={!canEdit} />
            </div>
          </FormSection>
        </Card>

        <Card>
          <FormSection legend={ar ? "الإقليم" : "Locale"}>
            <div className="grid gap-4 sm:grid-cols-3">
              <Select label={ar ? "اللغة الافتراضية" : "Default language"} value={form.language} onChange={(e) => set("language", e.target.value as "ar" | "en")} disabled={!canEdit}>
                <option value="ar">{ar ? "العربية" : "Arabic"}</option>
                <option value="en">{ar ? "الإنجليزية" : "English"}</option>
              </Select>
              <Input label={ar ? "العملة" : "Currency"} value={form.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} disabled={!canEdit} maxLength={3} />
              <Input label={ar ? "المنطقة الزمنية" : "Timezone"} value={form.timezone} onChange={(e) => set("timezone", e.target.value)} disabled={!canEdit} />
            </div>
          </FormSection>
        </Card>

        {saveError && <ErrorText>{saveError}</ErrorText>}

        {canEdit && (
          <div className="flex justify-end">
            <Button type="submit" loading={saving}>{ar ? "حفظ" : "Save"}</Button>
          </div>
        )}
      </form>

      {canExport && (
        <Card>
          <FormSection legend={ar ? "تصدير البيانات" : "Data export"}>
            <p className="mb-3 text-xs text-muted-foreground">
              {ar
                ? "نسخة كاملة من بيانات المنشأة بصيغة JSON — للأرشفة أو النقل. لا تتضمن كلمات المرور أو رموز الجلسات."
                : "A complete JSON copy of the business's data — for archiving or portability. Never includes passwords or session tokens."}
            </p>
            <Button variant="outline" icon={<Download className="size-4" />} loading={exporting} onClick={runExport}>
              {ar ? "تنزيل نسخة كاملة" : "Download full export"}
            </Button>
          </FormSection>
        </Card>
      )}
    </div>
  );
}
